import { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Camera, User, Mail, Calendar, LogOut, Bell, Lock, Eye, EyeOff, Cake } from 'lucide-react';
import PushNotificationToggle from '@/components/PushNotificationToggle';
import { computeAge, formatAge } from '@/lib/age';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Birthday state
  const [editingBirthday, setEditingBirthday] = useState(false);
  const [birthday, setBirthday] = useState(() => {
    const b = user?.birthday || '';
    // Convert old MM-DD format to YYYY-MM-DD for the date picker
    if (b && b.length === 5 && /^\d{2}-\d{2}$/.test(b)) {
      return `2000-${b}`;
    }
    return b;
  });
  const [savingBirthday, setSavingBirthday] = useState(false);
  
  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const languageFlags = {
    fr: '🇫🇷',
    ru: '🇷🇺',
    md: '🇲🇩',
    uk: '🇺🇦'
  };

  const locale = language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : language === 'uk' ? 'uk-UA' : 'fr-FR';

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error(t('selectImage'));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('imageTooLarge'));
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    
    try {
      const response = await axios.post(`${API}/users/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ photo_url: response.data.photo_url });
      toast.success(t('photoUpdated'));
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!name.trim()) {
      toast.error(t('nameRequired'));
      return;
    }
    
    try {
      const response = await axios.put(`${API}/users/profile`, { name: name.trim() });
      updateUser(response.data);
      setEditing(false);
      toast.success(t('profileUpdated'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleUpdateBirthday = async () => {
    if (!birthday) {
      const msg = { fr: 'Veuillez entrer votre date d\'anniversaire', ru: 'Введите дату рождения', md: 'Introduceți data nașterii', uk: 'Введіть дату народження' };
      toast.error(msg[language] || msg.fr);
      return;
    }
    
    setSavingBirthday(true);
    try {
      // Send full YYYY-MM-DD date for storage
      const response = await axios.put(`${API}/users/profile`, { birthday: birthday });
      updateUser(response.data);
      setEditingBirthday(false);
      const successMsg = { fr: 'Anniversaire enregistré !', ru: 'День рождения сохранён!', md: 'Ziua de naștere salvată!', uk: 'День народження збережено!' };
      toast.success(successMsg[language] || successMsg.fr);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSavingBirthday(false);
    }
  };

  // Format birthday for display
  const formatBirthdayDisplay = (bday) => {
    if (!bday) return null;
    // Handle YYYY-MM-DD format (full date with year)
    if (bday.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(bday)) {
      const [y, m, d] = bday.split('-');
      const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      const dateStr = date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
      const age = computeAge(bday);
      return age !== null ? `${dateStr} (${formatAge(age, language)})` : dateStr;
    }
    // Handle old MM-DD format
    if (bday.length === 5 && /^\d{2}-\d{2}$/.test(bday)) {
      const [month, day] = bday.split('-');
      const date = new Date(2000, parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    }
    return bday;
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      const msg = { fr: 'Veuillez remplir tous les champs', ru: 'Заполните все поля', md: 'Completați toate câmpurile', uk: 'Заповніть всі поля' };
      toast.error(msg[language] || msg.fr);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      const msg = { fr: 'Les mots de passe ne correspondent pas', ru: 'Пароли не совпадают', md: 'Parolele nu se potrivesc', uk: 'Паролі не збігаються' };
      toast.error(msg[language] || msg.fr);
      return;
    }
    
    if (newPassword.length < 6) {
      const msg = { fr: 'Le mot de passe doit contenir au moins 6 caractères', ru: 'Пароль должен содержать минимум 6 символов', md: 'Parola trebuie să aibă minim 6 caractere', uk: 'Пароль має містити мінімум 6 символів' };
      toast.error(msg[language] || msg.fr);
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      const successMsg = { fr: 'Mot de passe modifié avec succès !', ru: 'Пароль успешно изменён!', md: 'Parola a fost schimbată!', uk: 'Пароль успішно змінено!' };
      toast.success(successMsg[language] || successMsg.fr);
      
      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('error');
      toast.error(errorMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  const memberSince = user?.created_at 
    ? new Date(user.created_at).toLocaleDateString(locale, { year: 'numeric', month: 'long' })
    : '—';

  return (
    <div data-testid="profile-page" className="max-w-2xl mx-auto space-y-6 animate-fadeIn">
      <h1 className="page-header">{t('profileTitle')}</h1>
      
      {/* Photo Section */}
      <div className="card-base p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative">
            <div className="profile-photo">
              {user?.photo_url ? (
                <img src={user.photo_url} alt={user.name} />
              ) : (
                <div className="profile-photo-placeholder">
                  <User className="w-12 h-12" />
                </div>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-0 right-0 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-slate-900 hover:bg-amber-400 transition-colors shadow-lg"
              data-testid="upload-photo-btn"
            >
              {uploading ? (
                <div className="spinner w-4 h-4" />
              ) : (
                <Camera className="w-5 h-5" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
              data-testid="photo-input"
            />
          </div>
          
          <div className="text-center sm:text-left flex-1">
            <h2 className="text-2xl font-bold text-slate-900 uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {user?.name}
            </h2>
            <p className="text-slate-500">{user?.role === 'manager' ? t('manager') : user?.role === 'subcontractor' ? 'Sous-traitant' : t('employee')}</p>
          </div>
        </div>
      </div>
      
      {/* Info Section */}
      <div className="card-base p-6 space-y-6">
        <h3 className="section-title">{t('information')}</h3>
        
        <div className="form-group">
          <Label className="form-label flex items-center gap-2">
            <User className="w-4 h-4" />
            {t('fullName')}
          </Label>
          {editing ? (
            <div className="flex gap-3">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-base flex-1"
                data-testid="name-input"
              />
              <Button onClick={handleUpdateName} className="btn-primary" data-testid="save-name-btn">
                {t('save')}
              </Button>
              <Button onClick={() => { setEditing(false); setName(user?.name || ''); }} variant="outline" className="btn-secondary">
                {t('cancel')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-slate-900 font-medium">{user?.name}</span>
              <Button onClick={() => setEditing(true)} variant="ghost" size="sm" data-testid="edit-name-btn">
                {t('edit')}
              </Button>
            </div>
          )}
        </div>
        
        <div className="form-group">
          <Label className="form-label flex items-center gap-2">
            <Mail className="w-4 h-4" />
            {t('email')}
          </Label>
          <span className="text-slate-900 font-medium">{user?.email}</span>
        </div>
        
        <div className="form-group">
          <Label className="form-label flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {t('memberSince')}
          </Label>
          <span className="text-slate-900 font-medium">{memberSince}</span>
        </div>
        
        {/* Birthday Field */}
        <div className="form-group">
          <Label className="form-label flex items-center gap-2">
            <Cake className="w-4 h-4" />
            {({ fr: 'Date d\'anniversaire', ru: 'День рождения', md: 'Ziua de naștere', uk: 'День народження' }[language] || 'Date d\'anniversaire')}
          </Label>
          {editingBirthday ? (
            <div className="flex gap-3">
              <Input
                type="date"
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                className="input-base flex-1"
                data-testid="birthday-input"
              />
              <Button 
                onClick={handleUpdateBirthday} 
                disabled={savingBirthday}
                className="btn-primary" 
                data-testid="save-birthday-btn"
              >
                {savingBirthday ? <div className="spinner w-4 h-4" /> : t('save')}
              </Button>
              <Button 
                onClick={() => { setEditingBirthday(false); const b = user?.birthday || ''; setBirthday(b && b.length === 5 && /^\d{2}-\d{2}$/.test(b) ? `2000-${b}` : b); }} 
                variant="outline" 
                className="btn-secondary"
              >
                {t('cancel')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-slate-900 font-medium">
                {user?.birthday ? formatBirthdayDisplay(user.birthday) : (
                  <span className="text-slate-400 italic">
                    {({ fr: 'Non renseigné', ru: 'Не указан', md: 'Nespecificat', uk: 'Не вказано' }[language] || 'Non renseigné')}
                  </span>
                )}
              </span>
              <Button 
                onClick={() => setEditingBirthday(true)} 
                variant="ghost" 
                size="sm" 
                data-testid="edit-birthday-btn"
              >
                {user?.birthday ? t('edit') : ({ fr: 'Ajouter', ru: 'Добавить', md: 'Adaugă', uk: 'Додати' }[language] || 'Ajouter')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Password Change Section */}
      <div className="card-base p-6">
        <h3 className="section-title flex items-center gap-2">
          <Lock className="w-5 h-5" />
          {({ fr: 'Mot de passe', ru: 'Пароль', md: 'Parolă', uk: 'Пароль' }[language] || 'Mot de passe')}
        </h3>
        
        {!showPasswordSection ? (
          <Button 
            onClick={() => setShowPasswordSection(true)}
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="change-password-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            {({ fr: 'Modifier mon mot de passe', ru: 'Изменить пароль', md: 'Schimbă parola', uk: 'Змінити пароль' }[language] || 'Modifier mon mot de passe')}
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="form-group">
              <Label className="form-label">
                {({ fr: 'Mot de passe actuel', ru: 'Текущий пароль', md: 'Parola actuală', uk: 'Поточний пароль' }[language] || 'Mot de passe actuel')}
              </Label>
              <div className="relative">
                <Input
                  type={showCurrentPw ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input-base pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPw(!showCurrentPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <Label className="form-label">
                {({ fr: 'Nouveau mot de passe', ru: 'Новый пароль', md: 'Parolă nouă', uk: 'Новий пароль' }[language] || 'Nouveau mot de passe')}
              </Label>
              <div className="relative">
                <Input
                  type={showNewPw ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-base pr-10"
                  placeholder={({ fr: 'Minimum 6 caractères', ru: 'Минимум 6 символов', md: 'Minim 6 caractere', uk: 'Мінімум 6 символів' }[language] || 'Minimum 6 caractères')}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPw(!showNewPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <Label className="form-label">
                {({ fr: 'Confirmer le nouveau mot de passe', ru: 'Подтвердите новый пароль', md: 'Confirmă parola nouă', uk: 'Підтвердіть новий пароль' }[language] || 'Confirmer le nouveau mot de passe')}
              </Label>
              <Input
                type={showNewPw ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-base"
                data-testid="confirm-password-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                onClick={() => {
                  setShowPasswordSection(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                variant="outline"
                className="flex-1"
              >
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-password-btn"
              >
                {changingPassword ? (
                  <div className="spinner w-4 h-4" />
                ) : (
                  ({ fr: 'Enregistrer', ru: 'Сохранить', md: 'Salvează', uk: 'Зберегти' }[language] || 'Enregistrer')
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Language Section */}
      <div className="card-base p-6">
        <h3 className="section-title">{t('language')}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={() => setLanguage('fr')}
            className={`flex items-center gap-2 px-3 py-3 rounded-sm border-2 transition-colors overflow-hidden ${language === 'fr' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <span className="text-xl flex-shrink-0">🇫🇷</span>
            <span className="font-medium text-sm truncate">{t('french')}</span>
          </button>
          <button
            onClick={() => setLanguage('ru')}
            className={`flex items-center gap-2 px-3 py-3 rounded-sm border-2 transition-colors overflow-hidden ${language === 'ru' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <span className="text-xl flex-shrink-0">🇷🇺</span>
            <span className="font-medium text-sm truncate">{t('russian')}</span>
          </button>
          <button
            onClick={() => setLanguage('md')}
            className={`flex items-center gap-2 px-3 py-3 rounded-sm border-2 transition-colors overflow-hidden ${language === 'md' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <span className="text-xl flex-shrink-0">🇲🇩</span>
            <span className="font-medium text-xs truncate">{t('moldovan')}</span>
          </button>
          <button
            onClick={() => setLanguage('uk')}
            className={`flex items-center gap-2 px-3 py-3 rounded-sm border-2 transition-colors overflow-hidden ${language === 'uk' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <span className="text-xl flex-shrink-0">🇺🇦</span>
            <span className="font-medium text-sm truncate">{t('ukrainian')}</span>
          </button>
        </div>
      </div>

      {/* Notifications Section */}
      <div className="card-base p-6">
        <h3 className="section-title flex items-center gap-2">
          <Bell className="w-5 h-5" />
          {language === 'fr' ? 'Notifications' : 'Notifications'}
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          {language === 'fr' 
            ? 'Recevez des notifications pour les nouveaux messages et matériaux.' 
            : 'Receive notifications for new messages and materials.'}
        </p>
        <PushNotificationToggle />
      </div>
      
      {/* Actions Section */}
      <div className="card-base p-6">
        <h3 className="section-title">{t('actions')}</h3>
        <Button 
          onClick={logout} 
          variant="destructive" 
          className="w-full sm:w-auto flex items-center gap-2"
          data-testid="logout-btn-profile"
        >
          <LogOut className="w-4 h-4" />
          {t('logoutButton')}
        </Button>
      </div>
    </div>
  );
}
